import type { ApolloCache, ApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { MEDIA_FOR_MEDIA_GROUP_FRAGMENT } from '~/singlePageApp/screens/Project/MediaGroup/MediaForMediaGroupFragment.gql';
import type { NoOpFn } from '~/utilities/util';
import { noOpFn } from '~/utilities/util';
import type { Nullable } from '~/utilities/type-guards';
import { isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import { uploadTitleKey } from './EditableUploadName';
import type { MutationArgument, MutationResponse } from './types';
import type {
  CreateChannelEpisodeFromUploadMutationResult,
  CreateChannelEpisodeFromUploadMutationVariables,
} from './__gql/prebuiltEntityCreators.gql.gen';
import { CreateChannelEpisodeFromUploadDocument } from './__gql/prebuiltEntityCreators.gql.gen';

// eslint-disable-next-line rulesdir/no-gql-tags -- TODO: migrate to graphql-codegen
const fragment = gql`
  fragment UploadInstance__Media on Media {
    __typename
    id
    hashedId
    wistiaUrl
    thumbnailUrl
    numericId
  }
`;

// eslint-disable-next-line rulesdir/no-gql-tags -- TODO: migrate to graphql-codegen
export const CREATE_PROJECT_MEDIA_MUTATION = gql`
  mutation CreateMedia($input: CreateMediaInput!) {
    createMedia(input: $input) {
      media {
        id
        ...UploadInstance__Media
        ...MediaForMediaGroup__Media
        project {
          id
          mediaCount
          thumbnailUrls
          updatedAt
        }
      }
    }
  }

  ${fragment}
  ${MEDIA_FOR_MEDIA_GROUP_FRAGMENT}
`;

interface CreateChannelEpisodeFromUploadClient {
  mutate: (
    argument: MutationArgument<CreateChannelEpisodeFromUploadMutationVariables>,
  ) => Promise<CreateChannelEpisodeFromUploadMutationResult>;
}

export interface CreateProjectMediaClient {
  mutate: (argument: object) => Promise<
    MutationResponse<{
      createMedia: {
        media: {
          numericId: number;
          wistiaUrl: string;
          thumbnailUrl: string;
          hashedId: string;
          id: string;
          __typename: string;
        };
      };
    }>
  >;
}

export type CreateProjectMediaFileParams = {
  uploadId: string;
  bakeryHashedId: string;
  name: string;
  mediaGroupId: string;
};

export const createProjectMedia = ({
  projectId,
  mediaGroupId: explicitMediaGroupId,
  update = noOpFn,
}: {
  projectId: string;
  mediaGroupId: string | undefined;
  update: NoOpFn;
}) => {
  return async (
    client: CreateProjectMediaClient,
    { uploadId, bakeryHashedId, name, ...rest }: CreateProjectMediaFileParams,
  ): Promise<object> => {
    const mediaGroupId = isNonEmptyString(explicitMediaGroupId)
      ? explicitMediaGroupId
      : rest.mediaGroupId;

    const result = await client.mutate({
      mutation: CREATE_PROJECT_MEDIA_MUTATION,
      variables: {
        input: {
          bakeryHashedId,
          name: localStorage.getItem(uploadTitleKey(uploadId)) ?? name,
          projectId,
          mediaGroupId,
        },
      },
      update,
    });

    localStorage.removeItem(uploadTitleKey(uploadId));

    if (result.data) {
      const {
        createMedia: { media },
      } = result.data;

      return {
        status: 'success',
        attributes: {
          wistiaUrl: media.wistiaUrl,
          thumbnailUrl: media.thumbnailUrl,
          mediaHashedId: media.hashedId,
          mediaId: media.id,
          mediaType: media.__typename,
          mediaNumericId: media.numericId,
        },
      };
    }

    return {
      status: 'error',
      error: result.error,
    };
  };
};

export const createChannelEpisodeFromUpload = ({
  variables,
  update,
}: {
  variables: { channelId: string; projectId: Nullable<string> };
  update: (cache: ApolloCache<unknown>, sectionId: string) => void;
}) => {
  return async (
    client: CreateChannelEpisodeFromUploadClient,
    fileParams: {
      uploadId: string;
      positionInList: { listId: string; indexInExistingList: number };
      name: string;
      bakeryHashedId: string;
    },
  ): Promise<object> => {
    const episodeResult = await client.mutate({
      mutation: CreateChannelEpisodeFromUploadDocument,
      variables: {
        input: {
          ...variables,
          bakeryHashedId: fileParams.bakeryHashedId,
          channelSectionId: fileParams.positionInList.listId,
          episodeTitle:
            localStorage.getItem(uploadTitleKey(fileParams.uploadId)) ?? fileParams.name,
          positionInSection: fileParams.positionInList.indexInExistingList,
        },
      },
      update: (cache: ApolloCache<unknown>) => update(cache, fileParams.positionInList.listId),
    });

    localStorage.removeItem(uploadTitleKey(fileParams.uploadId));

    if (isNotNil(episodeResult.data)) {
      const { hashedId, id, media } =
        episodeResult.data.createChannelEpisodeFromUpload?.channelEpisode ?? {};
      return {
        status: 'success',
        attributes: {
          episodeHashedId: hashedId,
          episodeId: id,
          mediaId: media?.id,
        },
      };
    }
    return {
      status: 'error',
      error: episodeResult.error,
    };
  };
};

export const defaultOnFileUploaded = (
  _: ApolloClient<object>,
  { uploadedFileUrl, bakeryHashedId }: { uploadedFileUrl: string; bakeryHashedId: string },
): { status: string; attributes: { assetUrl: string; bakeryHashedId: string } } => {
  return {
    status: 'success',
    attributes: {
      assetUrl: uploadedFileUrl,
      bakeryHashedId,
    },
  };
};
